import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler'; // Import the state handler for temporal popups
import useTranslations from '@/i18n/useTranslations';
import AuthService from '@/services/auth.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/DeleteAccountDialog.css';

export default function DeleteAccountPopup({ open, setOpen }) {
  const t = useTranslations('common.shared.delete_account_dialog');
  // Get the state handler to trigger temporal popups
  const { stateHandler } = useStateHandler();
  
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Handler for the Delete button
  const onDeleteClick = async () => {
    // Trigger temporal popup for deletion process
    stateHandler.set("temporalPopup", { 
      text: t('popups.deletion_started'),
    });
    
    setLoading(true);
    await UserService.deleteAccount();
    setTimeout(() => {
      setLoading(false);
      setShowConfirmation(true);
    }, 2000); // 2 seconds delay
  };

  // Handler for the Stay button
  const onStayClick = () => {
    // Trigger temporal popup for staying
    stateHandler.set("temporalPopup", { 
      text: t('popups.stay_thanks'),
    });
    // Close the popup
    setOpen(false);
  };

  // Handler for closing the popup (used by the X icon and the Close button in confirmation mode)
  const onCloseClick = () => {
    if (!loading) {
      if (!showConfirmation) {
        setOpen(false);
      } else {
        AuthService.logout();
      }
    }
  };

  return (
    <GenericPopup
      open={open}
      className="popup-confirm-delete-account_popup"
      opacity
    >
      <div className="popup-confirm-delete-account-container">
        <div className="popup-confirm-delete-account_header">
          <Text className="popup-confirm-delete-account_header-text">
            {!showConfirmation ? t('titles.delete_account') : t('titles.account_deleted')}
          </Text>
          <XIcon
            className="popup-confirm-delete-account_close"
            onClick={onCloseClick}
          />
        </div>

        <Text className="popup-confirm-delete-account_text">
          {!showConfirmation ? (
            <>
              {t('description.initial_prefix')}
              <a className="popup-confirm-delete-account_link" href="mailto:contact@suarte.art">{t('description.contact_email')}</a>
            </>
          ) : (
            <>
              <p>
                {t('description.confirmation.p1')}
              </p>
              <p>
                {t('description.confirmation.p2')}
              </p>
              <p>
                {t('description.confirmation.p3_prefix')} <a className="popup-confirm-delete-account_link" href="mailto:contact@suarte.art">{t('description.contact_email_no_space')}</a>
              </p>
              <p>
                {t('description.confirmation.thank_you')}
              </p>
            </>
          )}
        </Text>

        <div className="popup-confirm-delete-account_buttons">
          {!showConfirmation ? (
            <>
              <ContinueButton
                onClick={onDeleteClick}
                disabled={loading}
                // Added class here
                className="continue-button_two font-size-medium">
                {t('buttons.delete')}
                {loading && (
                  <Loader inline
                    className="popup-confirm-delete-account_spinner"
                    thin
                  />
                )}
              </ContinueButton>
              <ContinueButton
                onClick={onStayClick}
                // And added here as well
                className="continue-button_two font-size-medium">
                {t('buttons.stay')}
              </ContinueButton>
            </>
          ) : (
            <ContinueButton onClick={onCloseClick} className="font-size-medium">
              {t('buttons.close')}
            </ContinueButton>
          )}
        </div>

      </div>
    </GenericPopup>
  );
}
